import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { DynamicsBatchEntry, DynamicsDiagnostics, QualityApi } from '../qualityApi';
import { ResolvedPeriod } from '../../PeriodPicker';
import { useDefaultBoardId } from '../../../../shared/hooks';
import { DynamicsChartId } from './dynamicsPrefs';

type DynamicsMetric = Parameters<typeof QualityApi.getDynamics>[1]['metric'];

/**
 * Maps the user-facing chart ids (A, B, C, …) onto the backend metric name
 * each chart consumes. The batch provider uses this table to figure out which
 * metrics to request when the user enables a new card.
 *
 * Kept co-located with the provider so a new chart only needs one place to
 * register the mapping (instead of threading a constant through three
 * modules).
 */
export const CHART_ID_TO_METRIC: Record<DynamicsChartId, DynamicsMetric> = {
  A: 'plan-done-debt-vf',
  B: 'bug-flow',
  C: 'bug-arrival',
  D: 'mttr',
  E: 'escaped',
  G: 'qa-throughput',
  H: 'reopens',
  I: 'predictability',
  J: 'bug-origin',
  K: 'escape-taxonomy',
};

interface BatchState {
  byMetric: Record<string, DynamicsBatchEntry | undefined>;
  loading: boolean;
  error: string | null;
}

interface BatchContextValue {
  /** Metric data already fetched by the batch (or undefined if the card wasn't requested). */
  get(metric: DynamicsMetric): DynamicsBatchEntry | undefined;
  loading: boolean;
}

const BatchContext = createContext<BatchContextValue | null>(null);

interface ProviderProps {
  metrics: DynamicsMetric[];
  groupBy: 'sprint' | 'release' | 'week' | 'month';
  lastN: number;
  resolvedPeriod: ResolvedPeriod | null;
  children: React.ReactNode;
}

export const DynamicsBatchProvider: React.FC<ProviderProps> = ({
  metrics,
  groupBy,
  lastN,
  resolvedPeriod,
  children,
}) => {
  const boardId = useDefaultBoardId();
  const [state, setState] = useState<BatchState>({ byMetric: {}, loading: false, error: null });

  // Stable key so we only refetch when something meaningful changes.
  const metricsKey = useMemo(() => [...metrics].sort().join(','), [metrics]);

  useEffect(() => {
    let cancelled = false;
    if (!metrics.length) {
      setState({ byMetric: {}, loading: false, error: null });
      return;
    }
    setState((s) => ({ ...s, loading: true, error: null }));
    (async () => {
      try {
        const res = await QualityApi.getDynamicsBatch(
          { period: null, resolvedPeriod },
          { metrics, groupBy, lastN, boardId }
        );
        if (cancelled) return;
        const byMetric: Record<string, DynamicsBatchEntry> = {};
        for (const entry of res.results ?? []) {
          byMetric[entry.metric] = entry;
        }
        setState({ byMetric, loading: false, error: null });
      } catch (err) {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : 'Failed to load dynamics batch';
        setState({ byMetric: {}, loading: false, error: msg });
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metricsKey, groupBy, lastN, resolvedPeriod?.startDate, resolvedPeriod?.endDate, boardId]);

  const value = useMemo<BatchContextValue>(
    () => ({
      get: (metric) => state.byMetric[metric],
      loading: state.loading,
    }),
    [state]
  );

  return <BatchContext.Provider value={value}>{children}</BatchContext.Provider>;
};

export interface DynamicsBatchConsumerResult {
  points: Array<{ period: string; [k: string]: number | string | null }> | null;
  diagnostics: DynamicsDiagnostics | null;
  error: { message: string; code: string | null } | null;
  loading: boolean;
  /** True when this metric was actually supplied by the batch (so the card should skip its own fetch). */
  served: boolean;
}

/**
 * Subscribes the current chart card to the batch provider. Returns `served=false`
 * when there is no provider in the tree or the metric wasn't in the batch, so
 * the card can fall back to its old individual fetch path.
 */
export function useDynamicsBatchEntry(metric: DynamicsMetric): DynamicsBatchConsumerResult {
  const ctx = useContext(BatchContext);
  if (!ctx) {
    return { points: null, diagnostics: null, error: null, loading: false, served: false };
  }
  const entry = ctx.get(metric);
  if (!entry) {
    return { points: null, diagnostics: null, error: null, loading: ctx.loading, served: false };
  }
  return {
    points: entry.points ?? [],
    diagnostics: entry.diagnostics ?? null,
    error: entry.error ?? null,
    loading: ctx.loading,
    served: true,
  };
}
